import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

import defaultConfigData from '../config/policy-modal-config.json';
import { PolicyModalProps, PolicyModalConfig } from '../types';

const defaultConfig = defaultConfigData as PolicyModalConfig;

/**
 * Policy Modal Component
 *
 * Affiche les conditions d'acceptation avec scroll obligatoire.
 * Le bouton OK est désactivé jusqu'à ce que l'utilisateur scrolle jusqu'en bas.
 *
 * Features:
 * - Contenu structuré avec titres et paragraphes
 * - Scroll obligatoire pour activer le bouton OK
 * - Design System compliant
 * - Configuration JSON
 * - Modal non-dismissible (pas de fermeture par clic extérieur)
 *
 * @example
 * ```tsx
 * <PolicyModal
 *   isOpen={showPolicy}
 *   onClose={() => setShowPolicy(false)}
 *   onAccept={handleAcceptPolicy}
 * />
 * ```
 */
export const PolicyModal = ({
  isOpen,
  onClose,
  onAccept,
  config = defaultConfig,
}: PolicyModalProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;

    // Tolérance de 10px pour mobile
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  };

  // Reset quand la modale s'ouvre
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex max-h-[80vh] max-w-3xl flex-col"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-livelinx-purple200">
            {config.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto scroll-smooth pr-4" onScroll={handleScroll}>
          <div className={config.styles.contentPadding}>
            {config.content.map((item, index) => {
              if (item.type === 'heading') {
                const HeadingTag = `h${item.level}` as 'h3' | 'h4';
                return (
                  <HeadingTag
                    key={index}
                    className={`font-heading ${
                      item.level === 3 ? 'text-xl' : 'text-lg'
                    } ${config.styles.headingColor} mb-3 mt-6 font-bold first:mt-0`}
                  >
                    {item.text}
                  </HeadingTag>
                );
              }

              return (
                <p
                  key={index}
                  className={`font-body ${config.styles.textColor} mb-4 leading-relaxed`}
                >
                  {item.text}
                </p>
              );
            })}
          </div>
        </div>

        <DialogFooter className="mt-4 border-t border-livelinx-lightgray100 pt-4">
          <Button
            onClick={onAccept}
            disabled={!hasScrolledToBottom}
            className="w-full bg-primary hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {config.buttons.accept}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
